import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { SubscribedCourseService } from '../../../service/SubscribedCourseService';
import StudentCourseDetail from '../StudentCourseDetail/StudentCourseDetail';
import './CourseStudents.css';

const CourseStudents = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [courseTitle, setCourseTitle] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [detailDialogVisible, setDetailDialogVisible] = useState(false);

    const loadStudents = useCallback(() => {
        const subscribedCourseService = new SubscribedCourseService();
        setLoading(true);
        subscribedCourseService.findAllByCourseId(courseId)
            .then(data => {
                setStudents(data || []);
                if (data && data.length > 0 && data[0].course) {
                    setCourseTitle(data[0].course.title);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading students:', error);
                setLoading(false);
            });
    }, [courseId]);

    useEffect(() => {
        loadStudents();
    }, [loadStudents]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const studentNameTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <div className="flex flex-column">
                    <span className="font-bold">{rowData.uuser?.displayName || rowData.uuser?.title}</span>
                    <span className="text-sm text-600">{rowData.uuser?.email}</span>
                </div>
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        if (rowData.isArchived) {
            return <Tag severity="danger" value="Arşivlendi" icon="pi pi-box" />;
        }
        if (rowData.completedTime) {
            return <Tag severity="success" value="Tamamlandı" icon="pi pi-check-circle" />;
        }
        return <Tag severity="info" value={rowData.status || "Devam Ediyor"} icon="pi pi-play-circle" />;
    };

    const progressTemplate = (rowData) => {
        const ratio = rowData.completionRatio || 0;
        return (
            <div className="flex flex-column gap-1">
                <ProgressBar value={ratio} style={{ height: '8px' }} />
                <span className="text-xs text-600">{ratio.toFixed(1)}%</span>
            </div>
        );
    };

    const enrollmentDateTemplate = (rowData) => {
        return <span>{formatDate(rowData.enrollmentTime)}</span>;
    };

    const lastAccessTemplate = (rowData) => {
        return <span>{formatDate(rowData.lastAccessedTime)}</span>;
    };

    const completedDateTemplate = (rowData) => {
        return <span>{formatDate(rowData.completedTime)}</span>;
    };

    const handleRowClick = (event) => {
        const student = event.data;
        if (student && student.uuser && student.uuser.id) {
            setSelectedStudent(student);
            setDetailDialogVisible(true);
        }
    };

    const header = (
        <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-3">
            <div className="flex flex-column gap-2">
                <h5 className="m-0" style={{ color: 'var(--highlight-text-color)' }}>Kursa Kayıtlı Öğrenciler</h5>
                {courseTitle && <span className="text-lg font-bold">{courseTitle}</span>}
                <span className="text-600">Toplam {students.length} öğrenci</span>
            </div>
            <Button
                label="Kurslarıma Dön"
                icon="pi pi-arrow-left"
                onClick={() => navigate(`/instructor/courses`)}
                outlined
            />
        </div>
    );

    return (
        <div className="course-students-page p-4">
            <Card>
                <DataTable
                    value={students}
                    loading={loading}
                    header={header}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    dataKey="id"
                    emptyMessage="Bu kursa henüz kayıtlı öğrenci bulunmuyor."
                    className="p-datatable-students"
                    stripedRows
                    showGridlines
                    size="small"
                    selectionMode="single"
                    onRowClick={handleRowClick}
                    rowHover>
                    <Column
                        header="Öğrenci"
                        body={studentNameTemplate}
                        style={{ minWidth: '250px' }}
                    />
                    <Column
                        header="Durum"
                        body={statusTemplate}
                        field="isArchived"
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="completionRatio"
                        header="İlerleme"
                        body={progressTemplate}
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="enrollmentTime"
                        header="Kayıt Tarihi"
                        body={enrollmentDateTemplate}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="lastAccessedTime"
                        header="Son Erişim"
                        body={lastAccessTemplate}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="completedTime"
                        header="Tamamlanma Tarihi"
                        body={completedDateTemplate}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                </DataTable>
            </Card>

            {/* Student Detail Dialog */}
            <Dialog
                visible={detailDialogVisible}
                onHide={() => setDetailDialogVisible(false)}
                header="Öğrenci Detayları"
                style={{ width: '95vw', maxWidth: '1400px' }}
                contentStyle={{ maxHeight: '75vh', overflow: 'auto' }}
                maximizable
                modal
                className="student-detail-dialog">
                {selectedStudent && (
                    <StudentCourseDetail
                        courseId={courseId}
                        userId={selectedStudent.uuser?.id}
                    />
                )}
            </Dialog>
        </div>
    );
};

export default CourseStudents;

